import { Image } from 'expo-image';
import { useState } from 'react';
import { Pressable, View, type ViewStyle } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useCompleteEonOneTutorial } from '@/api/partners/organizations/use-complete-eon-one-tutorial';
import { openLinkInBrowser } from '@/core/utils';
import { Button, Checkbox, showErrorMessage, Text } from '@/ui';
import { Car } from '@/ui/icons/car';
import { DesktopMacbook } from '@/ui/icons/desktop-macbook';
import { Dollar } from '@/ui/icons/dollar';

import { FeatureCarousel } from '../feature-carousel';

const darkLogoSrc = require('../../../../public/images/partners/eon-one/eonone-logo-dark.png');
const lightLogoSrc = require('../../../../public/images/partners/eon-one/eonone-logo-light.png');

const introSlideOneSrc = require('../../../../public/images/partners/eon-one/coming-soon/slide-1.png');
const introSlideTwoSrc = require('../../../../public/images/partners/eon-one/coming-soon/slide-2.png');
const introSlideThreeSrc = require('../../../../public/images/partners/eon-one/coming-soon/slide-3.png');
const costComparisonSrc = require('../../../../public/images/partners/eon-one/tutorial/slide-2-cost-comparison.png');

const CUSTOMER_CARDS = [
  require('../../../../public/images/partners/eon-one/tutorial/slide-4-customers-earnings.jpg'),
  require('../../../../public/images/partners/eon-one/tutorial/slide-4-customers-winback.jpg'),
];
const CUSTOMER_CARD_ASPECT_RATIO = 1024 / 866;

const FEATURE_CARDS = [
  require('../../../../public/images/partners/eon-one/tutorial/slide-3-card-tolls.png'),
  require('../../../../public/images/partners/eon-one/tutorial/slide-3-card-integrations.png'),
  require('../../../../public/images/partners/eon-one/tutorial/slide-3-card-branding.png'),
  require('../../../../public/images/partners/eon-one/tutorial/slide-3-card-payments.png'),
  require('../../../../public/images/partners/eon-one/tutorial/slide-3-card-network.png'),
  require('../../../../public/images/partners/eon-one/tutorial/slide-3-card-support.png'),
];
const FEATURE_CARD_ROWS = [
  FEATURE_CARDS.slice(0, 3),
  FEATURE_CARDS.slice(3, 6),
];
const FEATURE_CARD_ASPECT_RATIO = 1382 / 884;
const FEATURE_GRID_GAP = 16;

const SLIDE_GRIDS: Partial<
  Record<number, { rows: unknown[][]; aspectRatio: number }>
> = {
  2: { rows: FEATURE_CARD_ROWS, aspectRatio: FEATURE_CARD_ASPECT_RATIO },
  3: { rows: [CUSTOMER_CARDS], aspectRatio: CUSTOMER_CARD_ASPECT_RATIO },
};

function getFitCardSize({
  width,
  height,
  columns,
  rows,
  gap,
  aspectRatio,
}: {
  width: number;
  height: number;
  columns: number;
  rows: number;
  gap: number;
  aspectRatio: number;
}) {
  if (width <= 0 || height <= 0) return { width: 0, height: 0 };

  const widthConstrained = (width - (columns - 1) * gap) / columns;
  const heightConstrained = ((height - (rows - 1) * gap) * aspectRatio) / rows;
  const cardWidth = Math.min(widthConstrained, heightConstrained);

  return { width: cardWidth, height: cardWidth / aspectRatio };
}

const TERMS_URL = 'https://www.eonrides.com/legal/terms-privacy-policy';
const SUPPORT_PHONE_URL = 'tel:+18555987090';

const INTRO_FEATURES = [
  {
    title: "Booking Site That's 100% Yours",
    body: 'A fully white-labeled portal using your customized subdomain, branding (logo and colors).',
    icon: DesktopMacbook,
    iconSize: 26,
    image: introSlideOneSrc,
  },
  {
    title: 'You Own It, We Run It',
    body: 'Run your whole rental business from one dashboard. We handle insurance, keyless access, verification, payments, and support.',
    icon: Car,
    iconSize: 20,
    image: introSlideTwoSrc,
  },
  {
    title: 'Keep 80% of every rental.',
    body: 'You still keep the same insurance, verification, support, and payment processing services.',
    icon: Dollar,
    iconSize: 20,
    image: introSlideThreeSrc,
  },
] as const;

const SLIDE_COUNT = 4;

export default function EonOneTutorial() {
  const { styles, theme } = useStyles(stylesheet);
  const secondaryTextStyle = { color: theme.colors.text.secondary };

  const SLIDES = [
    {
      title: (
        <>
          Your Cars. Your Brand.{' '}
          <Text type="largeTitle" style={secondaryTextStyle}>
            Your own Rental Business.
          </Text>
        </>
      ),
      subtitle:
        'Eon gives you the foundation to white-label and customize as you need.',
      image: null,
    },
    {
      title: (
        <>
          <Text type="largeTitle" style={secondaryTextStyle}>
            Skip the cost{' '}
          </Text>
          of building it all yourself
        </>
      ),
      subtitle:
        'Disconnected tools drain your revenue and your time. Eon One connects them.',
      image: costComparisonSrc,
    },
    {
      title: <>Everything Eon gets right, now yours</>,
      subtitle:
        'Your own personal version of Eon, more powerful than anything on the market',
      image: null,
    },
    {
      title: (
        <>
          Customers who stay yours, and{' '}
          <Text type="largeTitle" style={secondaryTextStyle}>
            keep coming back
          </Text>
        </>
      ),
      subtitle:
        "Bring your own or import your list. They're attributed to you, permanently. EonOne keeps them coming back, automatically.",
      image: null,
    },
  ] as const;

  const [slideIndex, setSlideIndex] = useState(0);
  const [showOverlay, setShowOverlay] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const grid = SLIDE_GRIDS[slideIndex];
  const [gridSize, setGridSize] = useState({ width: 0, height: 0 });
  const gridCard = grid
    ? getFitCardSize({
        ...gridSize,
        columns: grid.rows[0].length,
        rows: grid.rows.length,
        gap: FEATURE_GRID_GAP,
        aspectRatio: grid.aspectRatio,
      })
    : { width: 0, height: 0 };

  const { mutateAsync: completeTutorial, isPending: isCompleting } =
    useCompleteEonOneTutorial();

  const handleNext = () => {
    if (slideIndex === SLIDE_COUNT - 1) {
      setShowOverlay(true);
      return;
    }
    setSlideIndex((index) => index + 1);
  };

  const handleBack = () => {
    setSlideIndex((index) => Math.max(index - 1, 0));
  };

  const handleGetStarted = async () => {
    try {
      await completeTutorial();
    } catch {
      showErrorMessage('Unable to complete the tutorial. Please try again.');
    }
  };

  const logoSrc =
    UnistylesRuntime.themeName === 'dark' ? darkLogoSrc : lightLogoSrc;

  if (showOverlay) {
    return (
      <View style={styles.overlay}>
        <Image
          source={logoSrc}
          style={styles.overlayLogo}
          contentFit="contain"
        />

        <Text type="largeTitle" style={styles.overlayHeadline}>
          A radical new way to scale your business is almost here.
        </Text>

        <Pressable
          style={styles.checkRow}
          onPress={() => setTermsAccepted((accepted) => !accepted)}
        >
          <Checkbox
            active={termsAccepted}
            onPress={() => setTermsAccepted((accepted) => !accepted)}
          />
          <Text type="bodyRegular">
            I agree of EonOne{' '}
            <Text
              type="bodyRegular"
              style={styles.termsLink}
              onPress={() => openLinkInBrowser(TERMS_URL)}
            >
              Terms and Conditions
            </Text>
          </Text>
        </Pressable>

        <Button
          text="Get started"
          type="primarySmall"
          onPress={handleGetStarted}
          disabled={!termsAccepted || isCompleting}
          loading={isCompleting}
          style={styles.overlayButton}
        />

        <Text
          type="caption1"
          style={{ color: theme.colors.text.secondary }}
          onPress={() => openLinkInBrowser(SUPPORT_PHONE_URL)}
        >
          Questions? Call or text (855) 598-7090
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.header}>
        <Image
          style={styles.logo}
          source={logoSrc}
          contentFit="contain"
          contentPosition="left center"
        />

        <View style={styles.copy}>
          <Text type="largeTitle">{SLIDES[slideIndex].title}</Text>

          <Text type="bodyRegular" style={secondaryTextStyle}>
            {SLIDES[slideIndex].subtitle}
          </Text>
        </View>
      </View>

      <View style={[styles.body, styles.bodyFill]}>
        {slideIndex === 0 ? (
          <FeatureCarousel features={INTRO_FEATURES} />
        ) : null}

        {SLIDES[slideIndex].image ? (
          <View
            style={[
              styles.slideImageWrap,
              {
                backgroundImage: `url("${
                  (SLIDES[slideIndex].image as { uri: string }).uri
                }")`,
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
              } as ViewStyle,
            ]}
          />
        ) : null}

        {grid ? (
          <View
            style={styles.cardGrid}
            onLayout={(event) => setGridSize(event.nativeEvent.layout)}
          >
            {grid.rows.map((row, rowIndex) => (
              <View key={rowIndex} style={styles.cardGridRow}>
                {row.map((card, cardIndex) => (
                  <View
                    key={cardIndex}
                    style={[
                      styles.cardGridCard,
                      {
                        width: gridCard.width,
                        height: gridCard.height,
                        backgroundImage: `url("${
                          (card as { uri: string }).uri
                        }")`,
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'cover',
                      } as ViewStyle,
                    ]}
                  />
                ))}
              </View>
            ))}
          </View>
        ) : null}
      </View>

      <View style={styles.nav}>
        <View style={styles.navSide} />

        <View style={styles.dots}>
          {Array.from({ length: SLIDE_COUNT }).map((_, index) => (
            <View
              key={index}
              style={[
                styles.dot,
                slideIndex === index ? styles.dotActive : styles.dotInactive,
              ]}
            />
          ))}
        </View>

        <View style={[styles.navSide, styles.navSideRight]}>
          {slideIndex !== 0 ? (
            <Button
              text="Back"
              type="ghost"
              onPress={handleBack}
              style={styles.backButton}
            />
          ) : null}
          <Button
            text="Next"
            type="primary"
            onPress={handleNext}
            style={styles.nextButton}
          />
        </View>
      </View>
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  content: {
    flexGrow: 1,
    paddingTop: 64,
    paddingBottom: 24,
    paddingHorizontal: 64,
    gap: 24,
    alignItems: 'flex-start',
  },
  header: {
    gap: 24,
    alignItems: 'flex-start',
  },
  logo: {
    width: 178,
    height: 36,
  },
  copy: {
    gap: 8,
  },
  body: {
    alignSelf: 'flex-start',
    width: '100%',
  },
  bodyFill: {
    flex: 1,
    minHeight: 0,
  },
  slideImageWrap: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    borderRadius: 16,
    overflow: 'hidden',
  },
  cardGrid: {
    flex: 1,
    minHeight: 0,
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: FEATURE_GRID_GAP,
  },
  cardGridRow: {
    flexDirection: 'row',
    gap: FEATURE_GRID_GAP,
  },
  cardGridCard: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  nav: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: 50,
  },
  navSide: {
    width: 316,
  },
  navSideRight: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 16,
  },
  backButton: {
    width: 150,
    height: 32,
  },
  nextButton: {
    width: 150,
    height: 50,
  },
  dots: {
    flexDirection: 'row',
    gap: 8,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  dotActive: {
    backgroundColor: theme.colors.text.primary,
  },
  dotInactive: {
    backgroundColor: theme.colors.text.secondary,
    opacity: 0.4,
  },
  overlay: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
    paddingHorizontal: 56,
    paddingVertical: 40,
  },
  overlayLogo: {
    width: 140,
    height: 36,
  },
  overlayHeadline: {
    textAlign: 'center',
    maxWidth: 596,
  },
  checkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  termsLink: {
    textDecorationLine: 'underline',
  },
  overlayButton: {
    width: 'auto',
    alignSelf: 'center',
    paddingLeft: 16,
    paddingRight: 16,
  },
}));
